import ApiClient from '@/domain/core/utils/apiClient'
import client_core from '@/services/clients/client_core'
const client = new ApiClient(client_core)

const endpoints = {
  list: '/dashboard/api/v3/email-templates/{scope}/templates/',
  detail: '/dashboard/api/v3/email-templates/{scope}/templates/{emailType}/',
  availableTypes: '/dashboard/api/v3/email-templates/{scope}/templates/available-types/',
  availableParameters: '/dashboard/api/v3/email-templates/{scope}/templates/parameters/',
  debugLogs: '/dashboard/api/v3/email-templates/{scope}/templates/{emailType}/debug-logs/',
  debugLogsExport: '/dashboard/api/v3/email-templates/{scope}/templates/{emailType}/debug-logs/export/',
  debugLogsSendToEmails: '/dashboard/api/v3/email-templates/{scope}/templates/{emailType}/debug-logs/send-to-emails/',
  debugLogsReplaySelected: '/dashboard/api/v3/email-templates/{scope}/templates/{emailType}/debug-logs/replay-selected/',
  debugLogsDeleteSelected: '/dashboard/api/v3/email-templates/{scope}/templates/{emailType}/debug-logs/delete-selected/',
  debugLogsMarkAsSent: '/dashboard/api/v3/email-templates/{scope}/templates/{emailType}/debug-logs/mark-as-sent/'
}

/**
 * Build scope path for API endpoints
 * @param {Object} scope - {type: 'global'|'contract-manager'|'event-owner'|'pica-service', id: number}
 * @returns {string} - Scope path for URL
 */
const buildScope = (scope) => {
  if (scope.type === 'global') {
    return 'global'
  } else if (scope.type === 'contract-manager') {
    return `contract-manager/${scope.id}`
  } else if (scope.type === 'event-owner') {
    return `event-owner/${scope.id}`
  } else if (scope.type === 'pica-service') {
    return `pica-service/${scope.id}`
  }
  throw new Error(`Invalid scope type: ${scope.type}`)
}

export default {
  /**
   * Fetch all email templates for a given scope
   * @param {Object} scope
   * @returns {Promise<Array>}
   */
  async fetchEmailTemplates(scope) {
    const scopePath = buildScope(scope)
    const url = endpoints.list.replace('{scope}', scopePath)
    return await client.get(url)
  },

  /**
   * Fetch a single email template by type
   * @param {Object} scope
   * @param {string} emailType
   * @returns {Promise<Object>}
   */
  async fetchEmailTemplate(scope, emailType) {
    const scopePath = buildScope(scope)
    const url = endpoints.detail
      .replace('{scope}', scopePath)
      .replace('{emailType}', emailType)
    return await client.get(url)
  },

  /**
   * Create a new email template
   * @param {Object} scope
   * @param {Object} data
   * @returns {Promise<Object>}
   */
  async createEmailTemplate(scope, data) {
    const scopePath = buildScope(scope)
    const url = endpoints.list.replace('{scope}', scopePath)
    return await client.post(url, data)
  },

  /**
   * Update an existing email template
   * @param {Object} scope
   * @param {string} emailType
   * @param {Object} data
   * @returns {Promise<Object>}
   */
  async updateEmailTemplate(scope, emailType, data) {
    const scopePath = buildScope(scope)
    const url = endpoints.detail
      .replace('{scope}', scopePath)
      .replace('{emailType}', emailType)
    return await client.put(url, data)
  },

  /**
   * Delete an email template
   * @param {Object} scope
   * @param {string} emailType
   * @returns {Promise<void>}
   */
  async deleteEmailTemplate(scope, emailType) {
    const scopePath = buildScope(scope)
    const url = endpoints.detail
      .replace('{scope}', scopePath)
      .replace('{emailType}', emailType)
    return await client.delete(url)
  },

  /**
   * Fetch available email types for a given scope
   * @param {Object} scope
   * @returns {Promise<Array>} - Array of available email types with their configurations
   */
  async fetchAvailableTypes(scope) {
    const scopePath = buildScope(scope)
    const url = endpoints.availableTypes.replace('{scope}', scopePath)
    return await client.get(url)
  },

  /**
   * Fetch available parameters for an email type
   * @param {Object} scope
   * @param {string} emailType
   * @returns {Promise<Object>} - Object with parameter definitions
   */
  async fetchAvailableParameters(scope, emailType) {
    const scopePath = buildScope(scope)
    const url = `${endpoints.availableParameters.replace('{scope}', scopePath)}?email_type=${emailType}`
    return await client.get(url)
  },

  /**
   * Fetch debug email logs for a given email template
   * @param {Object} scope
   * @param {string} emailType
   * @param {Object} params - Query parameters (page, page_size)
   * @returns {Promise<Object>} - Paginated response with results
   */
  async fetchDebugLogs(scope, emailType, params = {}) {
    const scopePath = buildScope(scope)
    const url = endpoints.debugLogs
      .replace('{scope}', scopePath)
      .replace('{emailType}', emailType)
    return await client.get(url, { params })
  },

  /**
   * Export debug logs to Excel file
   * @param {Object} scope
   * @param {string} emailType
   * @returns {Promise<void>} - Downloads file automatically
   */
  async exportDebugLogs(scope, emailType) {
    const scopePath = buildScope(scope)
    const url = endpoints.debugLogsExport
      .replace('{scope}', scopePath)
      .replace('{emailType}', emailType)
    await client.download(url)
  },

  /**
   * Send emails to specific email addresses (creates debug logs)
   * @param {Object} scope
   * @param {string} emailType
   * @param {Array<string>} emailAddresses - Array of email addresses
   * @param {boolean} sendOnlyPending - Only send to recipients who haven't received the email yet
   * @returns {Promise<Object>} - {success_count, error_count, errors, total_processed}
   */
  async sendToEmails(scope, emailType, emailAddresses, sendOnlyPending = true) {
    const scopePath = buildScope(scope)
    const url = endpoints.debugLogsSendToEmails
      .replace('{scope}', scopePath)
      .replace('{emailType}', emailType)
    return await client.post(url, {
      email_addresses: emailAddresses,
      send_only_pending: sendOnlyPending
    })
  },

  /**
   * Replay selected debug logs by IDs
   * @param {Object} scope
   * @param {string} emailType
   * @param {Array<number>} logIds - Array of log IDs to replay
   * @param {string|null} recipientAddress - Optional recipient address override
   * @returns {Promise<Object>} - {success_count, error_count, errors, total_processed}
   */
  async replaySelectedDebugLogs(scope, emailType, logIds, recipientAddress = null) {
    const scopePath = buildScope(scope)
    const url = endpoints.debugLogsReplaySelected
      .replace('{scope}', scopePath)
      .replace('{emailType}', emailType)
    const body = { log_ids: logIds }
    if (recipientAddress) {
      body.recipient_address = recipientAddress
    }
    return await client.post(url, body)
  },

  /**
   * Delete selected debug logs by IDs
   * @param {Object} scope
   * @param {string} emailType
   * @param {Array<number>} logIds - Array of log IDs to delete
   * @returns {Promise<Object>} - {deleted_count, message}
   */
  async deleteSelectedDebugLogs(scope, emailType, logIds) {
    const scopePath = buildScope(scope)
    const url = endpoints.debugLogsDeleteSelected
      .replace('{scope}', scopePath)
      .replace('{emailType}', emailType)
    return await client.post(url, { log_ids: logIds })
  },

  /**
   * Mark debug logs as sent without actually sending them
   * @param {Object} scope
   * @param {string} emailType
   * @param {Array<number>|null} logIds - Array of log IDs to mark, or null if using 'all'
   * @param {boolean} all - If true, mark all logs as sent
   * @returns {Promise<Object>} - {updated_count, message}
   */
  async markDebugLogsAsSent(scope, emailType, logIds = null, all = false) {
    const scopePath = buildScope(scope)
    const url = endpoints.debugLogsMarkAsSent
      .replace('{scope}', scopePath)
      .replace('{emailType}', emailType)
    const body = all ? { all: true } : { log_ids: logIds }
    return await client.post(url, body)
  }
}
